import { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuCheckCircle, LuXCircle } from 'react-icons/lu';
import { IoChevronDown } from 'react-icons/io5';
import type { IEarlyCheckinValidateResult } from '../../types';

interface EarlyCheckinTabProps {
  selectedTime: string;
  onTimeChange: (time: string) => void;
  currentCheckinTime: string;
  isValidating: boolean;
  validationResult: IEarlyCheckinValidateResult | null;
  isRtl: boolean;
}

/**
 * Formats a 24h "HH:00" string to Arabic 12h display.
 * e.g. "09:00" → "09:00 ص", "12:00" → "12:00 م (الظهر)", "13:00" → "01:00 م"
 */
const formatHourAr = (time24: string): string => {
  const hour = parseInt(time24.split(':')[0], 10);
  if (hour === 0) return '12:00 ص';
  if (hour < 12) return `${String(hour).padStart(2, '0')}:00 ص`;
  if (hour === 12) return '12:00 م (الظهر)';
  return `${String(hour - 12).padStart(2, '0')}:00 م`;
};

const formatHourEn = (time24: string): string => {
  const hour = parseInt(time24.split(':')[0], 10);
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${String(hour).padStart(2, '0')}:00 AM`;
  if (hour === 12) return '12:00 PM (Noon)';
  return `${String(hour - 12).padStart(2, '0')}:00 PM`;
};

const EarlyCheckinTab = ({ selectedTime, onTimeChange, currentCheckinTime, isValidating, validationResult, isRtl }: EarlyCheckinTabProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isValid = validationResult?.isValid === true;
  const isFailed = validationResult !== null && !validationResult.isValid;
  const message = validationResult
    ? (isAr ? validationResult.messageAr : validationResult.messageEn)
    : null;

  const borderColor = isFailed ? '#EF4444' : isValid ? '#22C55E' : '#E5E7EB';

  // Generate hourly options that are strictly before the scheduled check-in time.
  // Special case: midnight (00:00) check-in → show previous evening (18:00–23:00)
  //   because there are no valid same-day slots before 00:00.
  const isMidnightCheckin = useMemo(() => {
    const parsed = currentCheckinTime ? parseInt(currentCheckinTime.split(':')[0], 10) : NaN;
    return !isNaN(parsed) && parsed === 0;
  }, [currentCheckinTime]);

  const hourlyOptions = useMemo(() => {
    const parsed = currentCheckinTime ? parseInt(currentCheckinTime.split(':')[0], 10) : NaN;
    if (isNaN(parsed)) return [];
    if (parsed === 0) {
      // Midnight check-in: all valid early times are on the previous day
      return ['18:00', '19:00', '20:00', '21:00', '22:00', '23:00'];
    }
    // Normal: every hour strictly before the scheduled check-in hour
    const options: string[] = [];
    for (let h = 1; h < parsed; h++) {
      options.push(`${String(h).padStart(2, '0')}:00`);
    }
    return options;
  }, [currentCheckinTime]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatHour = isAr ? formatHourAr : formatHourEn;
  const selectedLabel = selectedTime ? formatHour(selectedTime) : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography
        sx={{
          fontFamily: 'Tajawal, sans-serif',
          fontWeight: 700,
          fontSize: '15px',
          color: '#435363',
          mb: '4px',
        }}
      >
        {t('TaskManagemnt.expectedCheckinTime')}
      </Typography>

      {/* Dropdown */}
      <Box ref={dropdownRef} sx={{ position: 'relative' }}>
        <Box
          onClick={() => setDropdownOpen((prev) => !prev)}
          sx={{
            width: '100%',
            height: '49px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '12px',
            border: `1px solid ${borderColor}`,
            borderRadius: '12px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '15px',
              fontWeight: 500,
              color: selectedTime ? '#261b6a' : '#8E98A1',
            }}
          >
            {selectedLabel || t('TaskManagemnt.selectTime')}
          </Typography>
          {isValidating ? (
            <CircularProgress size={20} sx={{ color: '#5B3FFF' }} />
          ) : (
            <IoChevronDown
              size={18}
              color="#5B3FFF"
              style={{
                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </Box>

        {/* Options list */}
        {dropdownOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '54px',
              left: 0,
              right: 0,
              maxHeight: '240px',
              overflowY: 'auto',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(50,50,71,0.08)',
              zIndex: 20,
              py: '4px',
            }}
          >
            {isMidnightCheckin && (
              <Box sx={{ px: '16px', py: '6px', borderBottom: '1px solid #F3F4F6' }}>
                <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '11px', fontWeight: 600, color: '#8E98A1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {isAr ? 'اليوم السابق' : 'Previous Day'}
                </Typography>
              </Box>
            )}
            {hourlyOptions.map((time) => {
              const isCurrentCheckin = time === currentCheckinTime?.substring(0, 5);
              const isSelected = time === selectedTime;
              return (
                <Box
                  key={time}
                  onClick={() => {
                    onTimeChange(time);
                    setDropdownOpen(false);
                  }}
                  sx={{
                    px: '16px',
                    py: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    backgroundColor: isSelected ? '#F3F0FF' : 'transparent',
                    '&:hover': { backgroundColor: '#F9F8FF' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '15px',
                      fontWeight: isCurrentCheckin || isSelected ? 600 : 400,
                      color: isCurrentCheckin || isSelected ? '#5B3FFF' : '#435363',
                    }}
                  >
                    {formatHour(time)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Validation feedback */}
      {!isValidating && isValid && selectedTime && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LuCheckCircle size={16} color="#22C55E" />
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#22C55E' }}>
            {message || t('TaskManagemnt.earlyCheckinAvailable')}
          </Typography>
        </Box>
      )}


      {!isValidating && isFailed && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '4px' }}>
          {/* Error message */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <LuXCircle size={16} color="#EF4444" style={{ flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#EF4444' }}>
              {message || t('TaskManagemnt.earlyCheckinUnavailable')}
            </Typography>
          </Box>

          {/* Conflict details */}
          {validationResult.conflictsWithPreviousBooking && (
            <Box sx={{ backgroundColor: '#FEF2F2', borderRadius: '8px', p: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', fontWeight: 600, color: '#991B1B' }}>
                {isAr ? 'يتعارض مع حجز سابق' : 'Conflicts with previous booking'}
              </Typography>
              {validationResult.previousBookingCheckOut && (
                <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#7F1D1D' }}>
                  {isAr ? 'موعد خروج الحجز السابق: ' : 'Previous checkout: '}
                  {new Date(validationResult.previousBookingCheckOut).toLocaleString(isAr ? 'ar-SA' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}
                </Typography>
              )}
              {validationResult.minutesAfterPreviousCheckOut !== null && (
                <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#7F1D1D' }}>
                  {isAr ? 'الفارق بالدقائق: ' : 'Gap (minutes): '}
                  {validationResult.minutesAfterPreviousCheckOut}
                </Typography>
              )}
            </Box>
          )}

          {validationResult.impactsPreviousGuest && !validationResult.conflictsWithPreviousBooking && (
            <Box sx={{ backgroundColor: '#FFFBEB', borderRadius: '8px', p: '12px' }}>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', fontWeight: 600, color: '#92400E' }}>
                {isAr ? 'قد يؤثر على الضيف السابق' : 'May impact previous guest'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EarlyCheckinTab;
